import PropTypes from 'prop-types';
import { useMemo, useRef, useState } from 'react';

import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';

import Transitions from './../../../../components/@extended/Transitions';
import Avatar from './../../../../components/@extended/Avatar';
import ProfileTab from './ProfileTab';
import SettingTab from './SettingTab';

import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import { useNavigate } from 'react-router';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
      {value === index && children}
    </div>
  );
}

function a11yProps(index) {
  return { id: `profile-tab-${index}`, 'aria-controls': `profile-tabpanel-${index}` };
}

export default function Profile() {
  const theme = useTheme();
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(0);

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };
  const handleChange = (_, newValue) => setValue(newValue);
  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
    window.location.reload();
  };

  const username = localStorage.getItem('username') || 'Admin';
  const useremail = localStorage.getItem('useremail') || '';
  const rolecode = localStorage.getItem('rolecode') || 0;
  const roleName = useMemo(() => {
    const data = JSON.parse(localStorage.getItem('roleData')) || [];
    if (!Array.isArray(data) || data.length === 0) return 'User';
    return data.find((item) => item.code === rolecode)?.role || 'User';
  }, [rolecode]);

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <Tooltip title="Profile" disableInteractive>
        <ButtonBase
          sx={{
            p: 0.25,
            borderRadius: 1,
            '&:focus-visible': { outline: `2px solid ${theme.vars?.palette.secondary.dark}`, outlineOffset: 2 }
          }}
          aria-label="open profile"
          ref={anchorRef}
          aria-controls={open ? 'profile-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Avatar
            alt="profile user"
            size="md"
            sx={{
              background: 'var(--appMainMain)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid',
              borderColor: 'var(--appMain200)',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              transition: 'all 0.15s ease',
              '&:hover': {
                boxShadow: '0 2px 10px rgba(30,58,95,0.4)',
                transform: 'scale(1.04)'
              }
            }}
          >
            <UserOutlined style={{ fontSize: 18 }} />
          </Avatar>
        </ButtonBase>
      </Tooltip>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
                width: 300,
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {/* ---- HERO ---- */}
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a8e 100%)',
                      px: 2.5,
                      pt: 2.5,
                      pb: 0,
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {/* bg circles */}
                    <Box
                      sx={{
                        position: 'absolute',
                        right: -30,
                        top: -30,
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.05)'
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        left: -20,
                        bottom: -20,
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.04)'
                      }}
                    />

                    {/* top row: avatar + logout */}
                    <Box
                      sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}
                    >
                      <Box sx={{ position: 'relative' }}>
                        {/* <Avatar
                          alt="profile user"
                          src={avatar1}
                          sx={{ width: 52, height: 52, borderRadius: 2, border: '2px solid rgba(255,255,255,0.3)' }}
                        /> */}
                        <Avatar
                          alt="profile user"
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: 2,
                            border: '2px solid rgba(255,255,255,0.3)',
                            bgcolor: 'rgba(255,255,255,0.15)',
                            color: '#fff'
                          }}
                        >
                          <UserOutlined style={{ fontSize: 24 }} />
                        </Avatar>
                        {/* online dot */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 2,
                            right: 2,
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: '#22c55e',
                            border: '2px solid #1e3a5f'
                          }}
                        />
                      </Box>

                      <Tooltip title="Logout">
                        <IconButton
                          size="small"
                          onClick={handleLogout}
                          sx={{
                            color: 'rgba(255,255,255,0.7)',
                            bgcolor: 'rgba(255,255,255,0.08)',
                            border: '1px solid rgba(255,255,255,0.15)',
                            borderRadius: 1.5,
                            width: 32,
                            height: 32,
                            '&:hover': { bgcolor: 'rgba(239,68,68,0.25)', color: '#fff', borderColor: 'rgba(239,68,68,0.5)' }
                          }}
                        >
                          <LogoutOutlined style={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* name / email / role */}
                    <Box sx={{ mt: 1.2, position: 'relative', zIndex: 1 }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{username}</Typography>
                      <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', mt: 0.3 }}>{useremail}</Typography>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mt: 0.8,
                          px: 1,
                          py: 0.3,
                          bgcolor: 'rgba(255,255,255,0.1)',
                          border: '1px solid rgba(255,255,255,0.18)',
                          borderRadius: 10
                        }}
                      >
                        <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>✦ {roleName}</Typography>
                      </Box>
                    </Box>

                    {/* tabs sitting on gradient */}
                    <Tabs
                      value={value}
                      onChange={handleChange}
                      variant="fullWidth"
                      sx={{
                        mt: 1.5,
                        minHeight: 38,
                        borderTop: '1px solid rgba(255,255,255,0.1)',
                        '& .MuiTab-root': {
                          color: 'rgba(255,255,255,0.5)',
                          fontSize: 12,
                          fontWeight: 600,
                          textTransform: 'none',
                          minHeight: 38,
                          py: 1,
                          gap: 0.6,
                          flexDirection: 'row',
                          '& .MuiTab-icon': { marginBottom: 0 }
                        },
                        '& .Mui-selected': { color: '#fff !important' },
                        '& .MuiTabs-indicator': { backgroundColor: '#fff', height: 2 }
                      }}
                    >
                      <Tab icon={<UserOutlined />} label="Profile" {...a11yProps(0)} />
                      <Tab icon={<SettingOutlined />} label="Settings" {...a11yProps(1)} />
                    </Tabs>
                  </Box>

                  {/* ---- PANELS ---- */}
                  <TabPanel value={value} index={0} dir={theme.direction}>
                    <ProfileTab />
                  </TabPanel>
                  <TabPanel value={value} index={1} dir={theme.direction}>
                    <SettingTab />
                  </TabPanel>

                  {/* ---- FOOTER ---- */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: 'rgba(0,0,0,0.02)'
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: 10, color: 'text.disabled' }}>
                      Zenigma Labs
                    </Typography>
                    <Typography
                      component="a"
                      href="https://zenigmalabs.com/"
                      target="_blank"
                      variant="caption"
                      sx={{ fontSize: 10, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      zenigmalabs.com ↗
                    </Typography>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}

TabPanel.propTypes = { children: PropTypes.node, value: PropTypes.number, index: PropTypes.number };
